import { useLayoutEffect, useState } from 'react';

interface UseTaskViewPortalParams {
  isSplitView: boolean;
  splitPortalNode: HTMLDivElement | null;
  visible: boolean;
}

// The portal target passed to createPortal never changes — React only
// preserves a portaled component's state while that target stays the same
// reference. So we move the container itself between the tab slot and the
// split drawer with plain DOM APIs instead, which React's reconciler never
// sees as a tree change.
export function useTaskViewPortal({
  isSplitView,
  splitPortalNode,
  visible,
}: UseTaskViewPortalParams) {
  const [portalContainer] = useState(() => document.createElement('div'));
  const [homeNode, setHomeNode] = useState<HTMLDivElement | null>(null);
  // The container starts detached and only reaches the DOM through the effect
  // below, which — being a parent's effect — runs after the mount effects of
  // anything the portal has already rendered. So the task view has to wait
  // for it: Quill resolves its toolbar with document.querySelector as it
  // mounts, and finds nothing while the container is still orphaned.
  const [isConnected, setIsConnected] = useState(false);

  useLayoutEffect(() => {
    const target = isSplitView && splitPortalNode ? splitPortalNode : homeNode;
    if (target && portalContainer.parentNode !== target) {
      target.appendChild(portalContainer);
    }
    setIsConnected(portalContainer.isConnected);
  }, [isSplitView, splitPortalNode, homeNode, portalContainer]);

  useLayoutEffect(() => {
    portalContainer.className = visible ? 'contents' : 'hidden';
  });

  return { portalContainer, setHomeNode, isConnected } as const;
}
